import React, { useCallback, useState, FC } from "react";

import { List, ListItem } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import moment from "moment-timezone";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Down24 } from "~/theme/icons/components/down";
import { Up24 } from "~/theme/icons/components/up";

import { HOURS_CLASSES } from "./constants";
import {
  getCommonSchedule,
  getCurrentDayData,
  getCurrentDayLabel,
  getHolidaysSchedule,
} from "./helpers";
import { HoursContainer } from "./styled";
import { HoursProps } from "./types";

export const Hours: FC<HoursProps> = ({
  classes: externalClasses,
  hours,
  timeZone,
  special,
  translations,
  regularSchedule,
}) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const { locale } = useParams<GlobalPathParams>();

  const toggleHoursVisible = useCallback(
    () => setDetailsVisible((detailsVisible) => !detailsVisible),
    [setDetailsVisible],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        toggleHoursVisible();
      }
    },
    [toggleHoursVisible],
  );

  if (!hours) {
    return null;
  }

  const { hoursText, holidayHoursText } = translations ?? {};

  const isHolidayHours = Array.isArray(hours);
  const now = typeof timeZone === "string" ? moment().tz(timeZone) : moment();

  const schedule = isHolidayHours
    ? getHolidaysSchedule({
        now,
        hours,
        timeZone,
        translations,
        regularSchedule,
        locale,
      })
    : getCommonSchedule({ now, hours, translations });

  const label = isHolidayHours ? holidayHoursText : hoursText;
  const currentDayLabel = getCurrentDayLabel({
    isHolidayHours,
    now,
    hours,
    translations,
  });
  const currentDayDetails = getCurrentDayData({
    isHolidayHours,
    now,
    hours,
    schedule,
    translations,
  });

  return (
    <HoursContainer
      className={externalClasses?.details}
      {...getTestAutomationProps("store-hours")}
    >
      <Typography
        variant="body1"
        className={clsx(HOURS_CLASSES.detailsLabel, externalClasses?.detailsLabel)}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        className={clsx(
          HOURS_CLASSES.detailsText,
          HOURS_CLASSES.detailsTextLink,
          externalClasses?.detailsText,
          externalClasses?.detailsTextLink,
        )}
        onClick={toggleHoursVisible}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        {...getTestAutomationProps("current-hours")}
      >
        {currentDayLabel}
        {!detailsVisible && (
          <Box component="span">
            &nbsp;
            {currentDayDetails}
          </Box>
        )}
        {detailsVisible ? <Up24 /> : <Down24 />}
      </Typography>
      {detailsVisible && (
        <Box
          className={clsx(HOURS_CLASSES.hours, externalClasses?.hours)}
          tabIndex={0}
          {...getTestAutomationProps("hours")}
        >
          <List component="ol">
            {schedule?.map(({ day, hours, current }) => (
              <ListItem
                key={day}
                className={
                  current
                    ? clsx(
                        HOURS_CLASSES.hoursText,
                        HOURS_CLASSES.hoursCurrent,
                        externalClasses?.hoursCurrent,
                      )
                    : clsx(HOURS_CLASSES.hoursText, externalClasses?.hoursText)
                }
              >
                <div>{day}</div> <div>{hours}</div>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      {special && (
        <Box
          className={clsx(HOURS_CLASSES.hoursSpecial, externalClasses?.hoursSpecial)}
          aria-hidden="true"
        >
          <Typography
            variant="body1"
            className={clsx(
              HOURS_CLASSES.hoursSpecialText,
              externalClasses?.hoursSpecialText,
            )}
            {...getTestAutomationProps("hours-special")}
          >
            {`*${special}`}
          </Typography>
        </Box>
      )}
    </HoursContainer>
  );
};
